// createSlab.js

class Point {
  constructor(x = 0.0, y = 0.0, z = 0.0) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  // 计算点的键值，考虑浮点数精度
  getKey() {
    return `${this.x.toFixed(6)},${this.y.toFixed(6)},${this.z.toFixed(6)}`;
  }
}

class Segment {
  constructor(p1, p2, guid) {
    this.p1 = p1; // 起点
    this.p2 = p2; // 终点
    this.guid = guid;
  }
}

class HalfEdge {
  // origin和destination都是Node类型
  // twin: HalfEdge (孪生半边)
  // next, prev: 用于环结构
  // angle: 半边的方向角
  // visited: 是否访问过
  // segment: 对应的Segment指针
  constructor(origin, destination, segment) {
    this.origin = origin;
    this.destination = destination;
    this.twin = null;
    this.next = null;
    this.prev = null;
    this.angle = 0.0;
    this.visited = false;
    this.segment = segment;
  }
}

class Node {
  constructor(point) {
    this.point = point;
    this.edges = []; // 存放从该点出发的所有HalfEdge
  }
}

class Polyline {
  constructor() {
    this.points = []; // 点列表
    this.guids = []; // 线段的GUID列表
  }

  // 计算多段线围成的区域面积（2D平面上，使用鞋带公式）
  area() {
    let sum = 0.0;
    const n = this.points.length;
    for (let i = 0; i < n; i++) {
      const p1 = this.points[i];
      const p2 = this.points[(i + 1) % n];
      sum += p1.x * p2.y - p2.x * p1.y;
    }
    return sum / 2.0;
  }
}

class CreateSlab {
  constructor() {}

  // 计算两个点之间的角度
  compute_angle(p0, p1) {
    return Math.atan2(p1.y - p0.y, p1.x - p0.x);
  }

  // 找到所有闭合多段线(只保留面积<0的内部多段线)
  findClosedPolylines(segments) {
    // 用Map存储点->Node，方便快速查找
    const pointNodeMap = new Map();
    const halfEdges = [];

    // 将所有Segment拆分成HalfEdge
    for (const seg of segments) {
      const p1 = seg.p1;
      const p2 = seg.p2;

      // 查找或创建node1
      let node1;
      const key1 = p1.getKey();
      if (pointNodeMap.has(key1)) {
        node1 = pointNodeMap.get(key1);
      } else {
        node1 = new Node(p1);
        pointNodeMap.set(key1, node1);
      }

      // 查找或创建node2
      let node2;
      const key2 = p2.getKey();
      if (pointNodeMap.has(key2)) {
        node2 = pointNodeMap.get(key2);
      } else {
        node2 = new Node(p2);
        pointNodeMap.set(key2, node2);
      }

      // 新建两个半边(双向)
      const he1 = new HalfEdge(node1, node2, seg);
      const he2 = new HalfEdge(node2, node1, seg);

      // 孪生关系
      he1.twin = he2;
      he2.twin = he1;

      // 计算方向角
      he1.angle = this.compute_angle(node1.point, node2.point);
      he2.angle = this.compute_angle(node2.point, node1.point);

      // 将半边放到node的出边列表
      node1.edges.push(he1);
      node2.edges.push(he2);

      // 存储
      halfEdges.push(he1);
      halfEdges.push(he2);
    }

    // 对每个node下的半边，按逆时针方向排序，并设置next和prev
    for (let node of pointNodeMap.values()) {
      node.edges.sort((a, b) => a.angle - b.angle);
      const n = node.edges.length;
      for (let i = 0; i < n; i++) {
        const curr = node.edges[i];
        const nxt = node.edges[(i + 1) % n];
        curr.next = nxt;
        nxt.prev = curr;
      }
    }

    // 找闭合多段线
    const polylines = [];
    for (const he of halfEdges) {
      if (he.visited) continue;

      const polyline = new Polyline();
      let startHE = he;
      let currHE = he;

      do {
        currHE.visited = true;
        polyline.points.push(currHE.origin.point);
        polyline.guids.push(currHE.segment.guid);
        currHE = currHE.next.twin;
        if (!currHE) break; // 防止死循环
      } while (currHE !== startHE && currHE !== null);

      // 仅当闭合才添加
      if (currHE === startHE) {
        polylines.push(polyline);
      }
    }

    // 根据面积(负为顺时针,视为内部)过滤
    const innerPolylines = [];
    for (const polyline of polylines) {
      const areaVal = polyline.area();
      if (areaVal < 0) {
        innerPolylines.push(polyline);
      }
    }
    return innerPolylines;
  }

  // 解析板数据字符串，生成Segment集合
  parseSlabData(strSlabData) {
    const lines = [];
    let data = strSlabData.trim();

    // 去除可能的尾部逗号
    if (data.endsWith(",")) {
      data = data.slice(0, -1);
    }

    // 使用正则表达式匹配所有 GUID 和对应的坐标数组
    const regex = /([a-f0-9\-]+):\[(.*?)\]/gi;
    let match;
    while ((match = regex.exec(data)) !== null) {
      const strGuid = match[1];
      const strPt = match[2];
      const ptArray = strPt
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      // 每3个坐标代表(x,y,z)
      if (ptArray.length % 3 === 0) {
        let start = new Point();
        let end = new Point();
        for (let g = 0; g < ptArray.length; g += 3) {
          const x1 = Math.round(parseFloat(ptArray[g]) * 10) / 10;
          const y1 = Math.round(parseFloat(ptArray[g + 1]) * 10) / 10;
          const z1 = Math.round(parseFloat(ptArray[g + 2]) * 10) / 10;
          if (g === 0) {
            start = new Point(x1, y1, z1);
            continue;
          }
          end = new Point(x1, y1, z1);
          lines.push(
            new Segment(
              new Point(start.x, start.y, start.z),
              new Point(end.x, end.y, end.z),
              strGuid
            )
          );
          // 更新start
          start = new Point(x1, y1, z1);
        }
      } else {
        console.warn(`GUID ${strGuid} 的坐标数据不完整，跳过。`);
      }
    }
    return lines;
  }

  // 根据多段线集合生成JSON字符串
  getJsonStr(allRegionArray) {
    if (allRegionArray.length === 0) return "";

    // GUID字符串 -> 对应Polyline列表
    const guidToRegions = {};

    for (const polyline of allRegionArray) {
      let guidString = "";
      for (const g of polyline.guids) {
        guidString += g + ",";
      }
      // 移除最后的逗号
      guidString = guidString.slice(0, -1);
      if (!guidToRegions[guidString]) {
        guidToRegions[guidString] = [];
      }
      guidToRegions[guidString].push(polyline);
    }

    // 构建JSON对象
    const jsonObj = { Slab: {} };
    for (const [guidKey, regions] of Object.entries(guidToRegions)) {
      jsonObj.Slab[guidKey] = regions.map((polyline) => ({
        "2DUpView": polyline.points
          .map((pt) => [
            parseFloat(pt.x.toFixed(6)),
            parseFloat(pt.y.toFixed(6)),
            parseFloat(pt.z.toFixed(6)),
          ])
          .flat(),
        "3DPrismPtData": polyline.points
          .map((pt) => [
            parseFloat(pt.x.toFixed(6)),
            parseFloat(pt.y.toFixed(6)),
            parseFloat(pt.z.toFixed(6)),
          ])
          .flat(),
        PrismDir: [0.0, 0.0, -1.0],
        modelHeight: 100.0,
        SectData: [
          polyline.points.length >= 2
            ? [
                `H,100,${polyline.points[0].x.toFixed(
                  6
                )},${polyline.points[0].y.toFixed(
                  6
                )},${polyline.points[0].z.toFixed(
                  6
                )},${polyline.points[1].x.toFixed(
                  6
                )},${polyline.points[1].y.toFixed(
                  6
                )},${polyline.points[1].z.toFixed(6)}`,
              ]
            : [],
        ],
      }));
    }

    return JSON.stringify(jsonObj, null, 2); // 格式化输出
  }

  // 从字符串数据解析 -> 找到闭合多段线 -> 生成JSON
  getSlabJsonData(strSalbData) {
    const Lines = this.parseSlabData(strSalbData);
    const allRegionArray = this.findClosedPolylines(Lines);
    const strJson = this.getJsonStr(allRegionArray);
    return strJson;
  }
}
export default CreateSlab;
